import { createSlice } from "@reduxjs/toolkit";
import {
  createProgressSummary,
  fetchProgressSummaries,
  fetchProgressSummaryById,
  updateProgressSummary,
  deleteProgressSummary,
  fetchClientProgressSummaries,
  autosaveProgressSummary,
  fetchMyDraftProgressSummaries
} from "../thunks/progressSummaryThunk";

const initialState = {
  progressSummaries: [],
  currentProgressSummary: null,
  clientProgressSummaries: {
    client: null,
    progressSummaries: []
  },
  // Autosave/Draft state
  drafts: [],
  autosaveId: null, // Track the ID of the current draft being edited
  autosaveStatus: 'idle', // 'idle' | 'saving' | 'saved' | 'error'
  lastAutosaved: null,
  pagination: {
    totalDocs: 0,
    limit: 10,
    totalPages: 0,
    page: 1,
    pagingCounter: 0,
    hasPrevPage: false,
    hasNextPage: false,
    prevPage: null,
    nextPage: null
  },
  loading: false,
  error: null,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false
};

const progressSummarySlice = createSlice({
  name: "progressSummary",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentProgressSummary: (state) => {
      state.currentProgressSummary = null;
    },
    clearClientProgressSummaries: (state) => {
      state.clientProgressSummaries = {
        client: null,
        progressSummaries: []
      };
    },
    setCurrentProgressSummary: (state, action) => {
      state.currentProgressSummary = action.payload;
    },
    resetLoadingStates: (state) => {
      state.createLoading = false;
      state.updateLoading = false;
      state.deleteLoading = false;
      state.loading = false;
    },
    setAutosaveId: (state, action) => {
      state.autosaveId = action.payload;
    },
    clearAutosaveState: (state) => {
      state.autosaveId = null;
      state.autosaveStatus = 'idle';
      state.lastAutosaved = null;
    },
    clearDrafts: (state) => {
      state.drafts = [];
    }
  },
  extraReducers: (builder) => {
    // Create progress summary
    builder
      .addCase(createProgressSummary.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createProgressSummary.fulfilled, (state, action) => {
        state.createLoading = false;
        state.progressSummaries.unshift(action.payload.progressSummary);
        state.currentProgressSummary = action.payload.progressSummary;
      })
      .addCase(createProgressSummary.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload?.message || "Failed to create progress summary";
      });

    // Fetch all progress summaries
    builder
      .addCase(fetchProgressSummaries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProgressSummaries.fulfilled, (state, action) => {
        state.loading = false;
        state.progressSummaries = action.payload.docs || action.payload;

        // Handle pagination if present
        if (action.payload.docs) {
          state.pagination = {
            totalDocs: action.payload.totalDocs,
            limit: action.payload.limit,
            totalPages: action.payload.totalPages,
            page: action.payload.page,
            pagingCounter: action.payload.pagingCounter,
            hasPrevPage: action.payload.hasPrevPage,
            hasNextPage: action.payload.hasNextPage,
            prevPage: action.payload.prevPage,
            nextPage: action.payload.nextPage
          };
        }
      })
      .addCase(fetchProgressSummaries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch progress summaries";
      });

    // Fetch progress summary by ID
    builder
      .addCase(fetchProgressSummaryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProgressSummaryById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProgressSummary = action.payload;
      })
      .addCase(fetchProgressSummaryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch progress summary";
      });

    // Update progress summary
    builder
      .addCase(updateProgressSummary.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateProgressSummary.fulfilled, (state, action) => {
        state.updateLoading = false;
        const updatedSummary = action.payload.progressSummary;

        // Update in the main list
        const index = state.progressSummaries.findIndex(
          summary => summary._id === updatedSummary._id
        );
        if (index !== -1) {
          state.progressSummaries[index] = updatedSummary;
        }

        // Update current summary if it's the same one
        if (state.currentProgressSummary?._id === updatedSummary._id) {
          state.currentProgressSummary = updatedSummary;
        }

        // Update in client progress summaries if present
        if (state.clientProgressSummaries?.progressSummaries?.docs) {
          const clientSummaryIndex = state.clientProgressSummaries.progressSummaries.docs.findIndex(
            summary => summary._id === updatedSummary._id
          );
          if (clientSummaryIndex !== -1) {
            state.clientProgressSummaries.progressSummaries.docs[clientSummaryIndex] = updatedSummary;
          }
        }
      })
      .addCase(updateProgressSummary.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload?.message || "Failed to update progress summary";
      });

    // Delete progress summary
    builder
      .addCase(deleteProgressSummary.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteProgressSummary.fulfilled, (state, action) => {
        state.deleteLoading = false;
        const deletedId = action.payload.progressSummaryId;

        // Remove from main list
        state.progressSummaries = state.progressSummaries.filter(
          summary => summary._id !== deletedId
        );

        // Clear current summary if it's the deleted one
        if (state.currentProgressSummary?._id === deletedId) {
          state.currentProgressSummary = null;
        }

        // Remove from client progress summaries if present
        if (state.clientProgressSummaries.progressSummaries.docs) {
          state.clientProgressSummaries.progressSummaries.docs =
            state.clientProgressSummaries.progressSummaries.docs.filter(
              summary => summary._id !== deletedId
            );
        }
      })
      .addCase(deleteProgressSummary.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload?.message || "Failed to delete progress summary";
      });

    // Fetch client progress summaries
    builder
      .addCase(fetchClientProgressSummaries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientProgressSummaries.fulfilled, (state, action) => {
        state.loading = false;
        state.clientProgressSummaries = action.payload;
      })
      .addCase(fetchClientProgressSummaries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch client progress summaries";
      });

    // Autosave progress summary
    builder
      .addCase(autosaveProgressSummary.pending, (state) => {
        state.autosaveStatus = 'saving';
      })
      .addCase(autosaveProgressSummary.fulfilled, (state, action) => {
        state.autosaveStatus = 'saved';
        state.lastAutosaved = new Date().toISOString();
        // Store the ID so subsequent autosaves update the same draft
        const savedSummary = action.payload.progressSummary;
        if (savedSummary?._id) {
          state.autosaveId = savedSummary._id;
        }
      })
      .addCase(autosaveProgressSummary.rejected, (state, action) => {
        state.autosaveStatus = 'error';
        // Don't show error for autosave failures - silent failure is preferred
        console.error('[ProgressSummary Slice] Autosave failed:', action.payload);
      });

    // Fetch my drafts
    builder
      .addCase(fetchMyDraftProgressSummaries.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMyDraftProgressSummaries.fulfilled, (state, action) => {
        state.loading = false;
        state.drafts = action.payload.drafts || [];
      })
      .addCase(fetchMyDraftProgressSummaries.rejected, (state, action) => {
        state.loading = false;
        state.drafts = [];
      });
  },
});

export const {
  clearError,
  clearCurrentProgressSummary,
  clearClientProgressSummaries,
  setCurrentProgressSummary,
  resetLoadingStates,
  setAutosaveId,
  clearAutosaveState,
  clearDrafts
} = progressSummarySlice.actions;

export default progressSummarySlice.reducer;